import { create } from 'zustand';
import { sitesService } from '@/services/sites';
import type { Site, SiteWithSnippet, CreateSiteForm, UpdateSiteForm } from '@/types';

interface SitesState {
  sites: Site[];
  currentSite: SiteWithSnippet | null;
  isLoading: boolean;
  error: string | null;

  fetchSites: (orgId: string) => Promise<void>;
  fetchSite: (siteId: string) => Promise<void>;
  setCurrentSite: (site: SiteWithSnippet | null) => void;
  createSite: (orgId: string, data: CreateSiteForm) => Promise<SiteWithSnippet>;
  updateSite: (siteId: string, data: UpdateSiteForm) => Promise<void>;
  deleteSite: (siteId: string) => Promise<void>;
  regenerateTrackingCode: (siteId: string) => Promise<void>;
  clearSites: () => void;
  clearError: () => void;
}

export const useSitesStore = create<SitesState>((set) => ({
  sites: [],
  currentSite: null,
  isLoading: false,
  error: null,

  fetchSites: async (orgId: string) => {
    set({ isLoading: true, error: null });
    try {
      const sites = await sitesService.list(orgId);
      set({ sites, isLoading: false });
    } catch (error) {
      set({ error: 'Failed to load sites', isLoading: false });
    }
  },

  fetchSite: async (siteId: string) => {
    set({ isLoading: true, error: null });
    try {
      const site = await sitesService.get(siteId);
      set({ currentSite: site, isLoading: false });
    } catch (error) {
      set({ error: 'Failed to load site', isLoading: false });
    }
  },

  setCurrentSite: (site) => {
    set({ currentSite: site });
  },

  createSite: async (orgId: string, data: CreateSiteForm) => {
    set({ isLoading: true, error: null });
    try {
      const site = await sitesService.create(orgId, data);
      set((state) => ({
        sites: [site, ...state.sites],
        currentSite: site,
        isLoading: false,
      }));
      return site;
    } catch (error) {
      set({ error: 'Failed to create site', isLoading: false });
      throw error;
    }
  },

  updateSite: async (siteId: string, data: UpdateSiteForm) => {
    set({ isLoading: true, error: null });
    try {
      const updated = await sitesService.update(siteId, data);
      set((state) => ({
        sites: state.sites.map((s) => (s.id === siteId ? updated : s)),
        currentSite:
          state.currentSite?.id === siteId
            ? { ...state.currentSite, ...updated }
            : state.currentSite,
        isLoading: false,
      }));
    } catch (error) {
      set({ error: 'Failed to update site', isLoading: false });
      throw error;
    }
  },

  deleteSite: async (siteId: string) => {
    set({ isLoading: true, error: null });
    try {
      await sitesService.delete(siteId);
      set((state) => ({
        sites: state.sites.filter((s) => s.id !== siteId),
        currentSite: state.currentSite?.id === siteId ? null : state.currentSite,
        isLoading: false,
      }));
    } catch (error) {
      set({ error: 'Failed to delete site', isLoading: false });
      throw error;
    }
  },

  regenerateTrackingCode: async (siteId: string) => {
    set({ isLoading: true, error: null });
    try {
      const updated = await sitesService.regenerateTrackingCode(siteId);
      set((state) => ({
        sites: state.sites.map((s) => (s.id === siteId ? updated : s)),
        currentSite: state.currentSite?.id === siteId ? updated : state.currentSite,
        isLoading: false,
      }));
    } catch (error) {
      set({ error: 'Failed to regenerate tracking code', isLoading: false });
      throw error;
    }
  },

  clearSites: () => {
    set({ sites: [], currentSite: null });
  },

  clearError: () => set({ error: null }),
}));

export default useSitesStore;
